import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Collapse,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { S3_URL_PRODUCTS } from "../utils/constants";
import { getOrderbyId } from "../services/authenticatedRequests";
import { useParams } from "react-router-dom";
import ComplaintSubmitModal from "./ComplaintSubmitModal";
import { trackOrder } from "../services/nonAuthRequets";
import jsPDF from "jspdf";
import { logoBase64Invoice } from "../utils/constants";
import LoadingComponent from "./LoadingComponent";
import NoResultsComponent from "./NoResultsComponent";
const OrderDetails = () => {
  const { id } = useParams(); // route: /order-details/:id
  const [order, setOrder] = useState(null);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Helper to trim product names to 20 characters
  const trimProductName = (name) => {
    return name && name.length > 50 ? name.substring(0, 50) + "..." : name;
  };

  // Helper to conditionally render a field if value exists
  const renderField = (label, value, prefix = "") => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <p className="small mb-1">
        <strong>{label}:</strong> {prefix}
        {value}
      </p>
    );
  };

  useEffect(() => {
    setLoading(true);
    const fetchOrder = async () => {
      try {
        const response = await getOrderbyId(id);
        setOrder(response);
      } catch (error) {
        console.error("Error fetching order:", error);
      }
      finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleDownloadInvoice = async () => {
    try {
      setInvoiceLoading(true);
      const response = await trackOrder(order.orderId);
      //console.log("Order details:", response);
      setInvoiceLoading(false);

      // Initialize jsPDF with A4 dimensions (portrait)
      const doc = new jsPDF("p", "mm", "a4");
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let y = margin;

      // Helper: Check if there is enough space; if not, add a new page.
      const checkPageBreak = (neededSpace = 10) => {
        if (y + neededSpace > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
      };

      // Helper: Add text that may span multiple lines, handling wrapping and page breaks.
      // Returns the updated y position.
      const addWrappedText = (text, x, maxWidth, lineHeight = 6) => {
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach((line) => {
          checkPageBreak(lineHeight);
          doc.text(line, x, y);
          y += lineHeight;
        });
        return y;
      };

      // === Invoice Header ===
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      checkPageBreak(15);
      // Move the logo to the top-right corner.
      const logoWidth = 50;
      const logoHeight = 15;
      const logoX = pageWidth - margin - logoWidth;
      doc.addImage(logoBase64Invoice, "PNG", logoX, y, logoWidth, logoHeight);
      // Place the "Invoice" title at the left.
      doc.text("Invoice", margin, y + logoHeight);
      y += logoHeight + 10;

      // === Order Basic Details ===
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      checkPageBreak(30);
      doc.text(`Order Number: ${response.orderNumber}`, margin, y);
      y += 6;
      doc.text(
        `Date: ${new Date(response.createdAt).toLocaleDateString()}`,
        margin,
        y
      );
      y += 6;
      doc.text(`Payment Method: ${response.paymentMethod}`, margin, y);
      y += 6;
      doc.text(`Status: ${response.status}`, margin, y);
      y += 10;

      // === Billing and Shipping Information (Side by Side) ===
      checkPageBreak(40);
      // Calculate column widths for two side-by-side blocks.
      const columnWidth = (pageWidth - 2 * margin) / 2;
      const billingX = margin;
      const shippingX = margin + columnWidth;
      let billY = y;
      let shipY = y;

      // Headers for each column.
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Billing Information", billingX, billY);
      doc.text("Shipping Information", shippingX, shipY);
      billY += 6;
      shipY += 6;

      // Billing details.
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Name: ${response.billingFirstName} ${response.billingLastName}`,
        billingX,
        billY
      );
      billY += 6;
      doc.text(`Email: ${response.billingEmail}`, billingX, billY);
      billY += 6;
      doc.text(`Phone: ${response.billingPhone}`, billingX, billY);
      billY += 6;
      const billingAddress = `Address: ${response.billingAddress1}, ${response.billingCity}, ${response.billingState}, ${response.billingPostcode}, ${response.billingCountry}`;
      const billingAddressLines = doc.splitTextToSize(
        billingAddress,
        columnWidth
      );
      billingAddressLines.forEach((line) => {
        checkPageBreak(6);
        doc.text(line, billingX, billY);
        billY += 6;
      });

      // Shipping details.
      doc.text(
        `Name: ${response.shippingFirstName} ${response.shippingLastName}`,
        shippingX,
        shipY
      );
      shipY += 6;
      doc.text(`Email: ${response.shippingEmail}`, shippingX, shipY);
      shipY += 6;
      doc.text(`Phone: ${response.shippingPhone}`, shippingX, shipY);
      shipY += 6;
      const shippingAddress = `Address: ${response.shippingAddress1}, ${response.shippingCity}, ${response.shippingState}, ${response.shippingPostcode}, ${response.shippingCountry}`;
      const shippingAddressLines = doc.splitTextToSize(
        shippingAddress,
        columnWidth
      );
      shippingAddressLines.forEach((line) => {
        checkPageBreak(6);
        doc.text(line, shippingX, shipY);
        shipY += 6;
      });

      // Move y to the lower end of the two columns.
      y = Math.max(billY, shipY) + 4;

      // === Order Items Table with Borders ===
      checkPageBreak(20);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const tableX = margin;
      let tableY = y;
      const tableWidth = pageWidth - 2 * margin;
      const col1Width = tableWidth * 0.6; // Name column
      const col2Width = tableWidth * 0.2; // Quantity column
      const col3Width = tableWidth * 0.2; // Price column

      // Draw table header with borders.
      const headerHeight = 6;
      // Header texts with a bit of padding.
      doc.text("Name", tableX + 2, tableY + 4);
      doc.text("Quantity", tableX + col1Width + 2, tableY + 4);
      doc.text("Price", tableX + col1Width + col2Width + 2, tableY + 4);
      // Draw header rectangle border.
      doc.rect(tableX, tableY, tableWidth, headerHeight);
      // Draw vertical lines in header.
      doc.line(
        tableX + col1Width,
        tableY,
        tableX + col1Width,
        tableY + headerHeight
      );
      doc.line(
        tableX + col1Width + col2Width,
        tableY,
        tableX + col1Width + col2Width,
        tableY + headerHeight
      );
      tableY += headerHeight;

      doc.setFont("helvetica", "normal");
      // Render each product as a table row with borders.
      response.products.forEach((product) => {
        // Wrap the product name if needed, subtracting padding.
        const nameLines = doc.splitTextToSize(
          product.productName,
          col1Width - 4
        );
        const rowHeight = Math.max(nameLines.length * 6, 6) + 2; // adding vertical padding
        checkPageBreak(rowHeight);
        const rowY = tableY;

        // Draw cell border for the row.
        doc.rect(tableX, rowY, tableWidth, rowHeight);
        // Draw vertical dividing lines.
        doc.line(
          tableX + col1Width,
          rowY,
          tableX + col1Width,
          rowY + rowHeight
        );
        doc.line(
          tableX + col1Width + col2Width,
          rowY,
          tableX + col1Width + col2Width,
          rowY + rowHeight
        );

        // Print product name (with padding).
        let currentLineY = rowY + 4;
        nameLines.forEach((line) => {
          doc.text(line, tableX + 2, currentLineY);
          currentLineY += 6;
        });
        // Print quantity and price in their cells.
        doc.text(product.quantity.toString(), tableX + col1Width + 2, rowY + 4);
        doc.text(
          "LKR " + product.price.toFixed(2),
          tableX + col1Width + col2Width + 2,
          rowY + 4
        );

        tableY += rowHeight;
      });
      y = tableY + 4;

      // Define two X positions near the right side for label & price
      const summaryLabelX = pageWidth - margin - 50; // Adjust "50" as needed
      const summaryPriceX = pageWidth - margin;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      // Subtotal
      doc.text("Subtotal:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.totalAmount.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Delivery Fee
      doc.text("Delivery Fee:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.deliveryFee.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Discount
      doc.text("Discount:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.orderDiscount.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Tax
      doc.text("Tax:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.orderTax.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Bold the Total row
      doc.setFont("helvetica", "bold");
      doc.text("Total:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.netAmount.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      doc.setFont("helvetica", "normal");
      y += 6;

      // Draw a horizontal line separator
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      // Footer Section (printed right below the summary)
      doc.text("https://bigmall.lk/", margin, y);
      y += 6;
      doc.text("Email: info@bigmall.lk", margin, y);
      y += 6;
      doc.text("Tel: (+94) 777961772 / (+94) 741337427", margin, y);

      // Generate a dynamic file name with order id and current timestamp.
      const timestamp = new Date().getTime();
      const fileName = `invoice_${order.orderId}_${timestamp}.pdf`;
      //doc.save(fileName);
      doc.output("dataurlnewwindow");
    } catch (error) {
      console.error(
        "Error fetching order details or generating invoice:",
        error
      );
      setInvoiceLoading(false);
    }
    finally {
      setInvoiceLoading(false);
    }
  };

  if (loading) {
    return <LoadingComponent message="Order details loading..." />;
  }

  if (!order) {
    return <NoResultsComponent message="Order not found." />;
  }

  document.title =  `Order Details ${order.orderId} | Bigmall`;

  return (
    <Container className="my-4">
      {/* First Row: Order Summary & Addresses */}
      <Row className="mb-4">
        {/* Order Summary Card */}
        <Col xs={12} md={4}>
          <Card className="h-100">
            <Card.Header>
              <h4 className="mb-0" style={{ textAlign: "left" }}>
                Order Summary
              </h4>
            </Card.Header>
            <Card.Body className="small">
              {order.orderId && (
                <p>
                  <strong>Order ID:</strong> {order.orderId}
                </p>
              )}
              {order.paymentMethodTitle && (
                <p>
                  <strong>Payment Method:</strong> {order.paymentMethodTitle}
                </p>
              )}
              {order.status && (
                <p>
                  <strong>Status:</strong>{" "}
                  <span className="badge bg-secondary">
                    {order.status.toUpperCase()}
                  </span>
                </p>
              )}
              {order.createdAt && (
                <p>
                  <strong>Created At:</strong>{" "}
                  {new Date(order.createdAt).toLocaleString()}
                </p>
              )}
              {order.customerNote && (
                <p>
                  <strong>Customer Note:</strong> {order.customerNote}
                </p>
              )}
              <button
                className="btn btn-outline-main btn-sm text-white text-sm mt-4"
                onClick={handleDownloadInvoice}
                disabled={invoiceLoading}
              >
                {invoiceLoading ? "Generating Invoice..." : "View Invoice"}
              </button>
            </Card.Body>
          </Card>
        </Col>

        {/* Billing Address Card */}
        <Col xs={12} md={4}>
          <Card className="h-100">
            <Card.Header>
              <h4 className="mb-0" style={{ textAlign: "left" }}>
                Billing Address
              </h4>
            </Card.Header>
            <Card.Body className="small">
              {order.billingAddress && (
                <>
                  {(order.billingAddress.firstName ||
                    order.billingAddress.lastName) && (
                    <p className="mb-1">
                      <strong>Name:</strong> {order.billingAddress.firstName}{" "}
                      {order.billingAddress.lastName}
                    </p>
                  )}
                  {order.billingAddress.address1 && (
                    <p className="mb-1">
                      <strong>Address:</strong> {order.billingAddress.address1}{" "}
                      {order.billingAddress.address2}
                    </p>
                  )}
                  {order.billingAddress.city && (
                    <p className="mb-1">
                      <strong>City:</strong> {order.billingAddress.city}
                    </p>
                  )}
                  {order.billingAddress.state && (
                    <p className="mb-1">
                      <strong>State:</strong> {order.billingAddress.state}
                    </p>
                  )}
                  {order.billingAddress.postCode && (
                    <p className="mb-1">
                      <strong>Post Code:</strong>{" "}
                      {order.billingAddress.postCode}
                    </p>
                  )}
                  {order.billingAddress.country && (
                    <p className="mb-1">
                      <strong>Country:</strong> {order.billingAddress.country}
                    </p>
                  )}
                  {order.billingAddress.email && (
                    <p className="mb-1">
                      <strong>Email:</strong> {order.billingAddress.email}
                    </p>
                  )}
                  {order.billingAddress.phone && (
                    <p className="mb-1">
                      <strong>Phone:</strong> {order.billingAddress.phone}
                    </p>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Shipping Address Card */}
        <Col xs={12} md={4}>
          <Card className="h-100">
            <Card.Header>
              <h4 className="mb-0" style={{ textAlign: "left" }}>
                Shipping Address
              </h4>
            </Card.Header>
            <Card.Body className="small">
              {order.shippingAddress && (
                <>
                  {(order.shippingAddress.firstName ||
                    order.shippingAddress.lastName) && (
                    <p className="mb-1">
                      <strong>Name:</strong> {order.shippingAddress.firstName}{" "}
                      {order.shippingAddress.lastName}
                    </p>
                  )}
                  {order.shippingAddress.address1 && (
                    <p className="mb-1">
                      <strong>Address:</strong> {order.shippingAddress.address1}{" "}
                      {order.shippingAddress.address2}
                    </p>
                  )}
                  {order.shippingAddress.city && (
                    <p className="mb-1">
                      <strong>City:</strong> {order.shippingAddress.city}
                    </p>
                  )}
                  {order.shippingAddress.state && (
                    <p className="mb-1">
                      <strong>State:</strong> {order.shippingAddress.state}
                    </p>
                  )}
                  {order.shippingAddress.postCode && (
                    <p className="mb-1">
                      <strong>Post Code:</strong>{" "}
                      {order.shippingAddress.postCode}
                    </p>
                  )}
                  {order.shippingAddress.country && (
                    <p className="mb-1">
                      <strong>Country:</strong> {order.shippingAddress.country}
                    </p>
                  )}
                  {order.shippingAddress.email && (
                    <p className="mb-1">
                      <strong>Email:</strong> {order.shippingAddress.email}
                    </p>
                  )}
                  {order.shippingAddress.phone && (
                    <p className="mb-1">
                      <strong>Phone:</strong> {order.shippingAddress.phone}
                    </p>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Items: Each item appears in its own row */}
      <Row className="mb-4">
        <Col xs={12}>
          <h4 className="mb-3" style={{ textAlign: "left" }}>
            Order Items
          </h4>
          {order.orderItems.map((item, idx) => (
            <Card key={idx} className="mb-3">
              <Card.Body className="small">
                <div className="d-flex align-items-center">
                  <img
                    src={`${S3_URL_PRODUCTS}${item.productImages[0]}`}
                    alt={item.productName}
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                      marginRight: "10px",
                    }}
                  />
                  <div>
                    <p className="col-auto" style={{ width: "150px" }}>
                      <strong>{trimProductName(item.productName)}</strong>
                    </p>
                  </div>
                  <div className="ms-auto">
                    <p className="">
                      <strong>Qty:</strong> {item.quantity}
                    </p>
                  </div>
                  <div className="ms-auto">
                    <p className="mb-0">
                      <strong>Price:</strong> LKR {item.price}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </Col>
      </Row>

      {/* Payment Summary: Right aligned below the order items */}
      <Row className="mb-4">
        <Col xs={12} className="d-flex justify-content-end">
          <div className="text-end">
            <div className="fw-bold mb-2">Total Summary</div>
            {order.discount
              ? renderField("Discount", order.discount, "LKR:")
              : null}
            {order.tax ? renderField("Tax", order.tax, "LKR:") : null}
            {order.deliveryFee
              ? renderField("Delivery Fee", order.deliveryFee, "LKR:")
              : null}
            {renderField("Total Amount", order.totalAmount, "LKR:")}
            <hr />
            <p className="small">
              <strong>Net Amount:</strong> LKR:{order.netAmount}
            </p>
          </div>
        </Col>
      </Row>

      {/* Complaint Handling */}
      {order.complaint && (
        <Row className="mb-4">
          <Col xs={12}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setComplaintOpen(!complaintOpen)}
              aria-controls="complaint-collapse"
              aria-expanded={complaintOpen}
            >
              {complaintOpen ? "Hide Complaint" : "View Complaint"}
            </Button>
            <Collapse in={complaintOpen}>
              <div id="complaint-collapse" className="mt-3">
                <Card>
                  <Card.Body className="small">
                    <div className="mb-2">
                      <span className="badge bg-secondary">
                        {order.complaint.status.toUpperCase()}
                      </span>
                    </div>
                    <div>{order.complaint.content}</div>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>
          </Col>
        </Row>
      )}

      {/* Submit a Complain Button */}
      <Row className="mb-4">
        <Col xs={12} className="text-center">
          {order.complaint ? (
            <OverlayTrigger
              overlay={
                <Tooltip id="tooltip-disabled">
                  Disabled because order already has a complaint. Order can have
                  one complaint.
                </Tooltip>
              }
            >
              <span className="d-inline-block">
                <Button
                  className="bg-main-600 text-white"
                  size="sm"
                  disabled
                  style={{ pointerEvents: "none" }}
                >
                  Submit a Complain
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              className="bg-main-600 text-white"
              size="sm"
              onClick={() => setOpenModal(true)}
            >
              Submit a Complain
            </Button>
          )}
        </Col>
      </Row>
      <ComplaintSubmitModal
        orderNumber={order.orderId}
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
      />
    </Container>
  );
};

export default OrderDetails;
